import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from 'libphonenumber-js/core';
import metadata from 'libphonenumber-js/metadata.full.json';

import type { PhoneNumber } from '@/types/crm';

const SUPPORTED_COUNTRIES = new Set<string>(
  getCountries(metadata as any) // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires its own metadata type; our JSON import is structurally identical
);

/**
 * Flag emoji for an ISO 3166-1 alpha-2 country code.
 *
 * Returns null — not a placeholder glyph — when the code is missing or isn't a
 * country libphonenumber knows about. A phone property can carry an empty or
 * junk `country_code` (see the fallbacks in {@link formatPhoneNumber}), and the
 * regional-indicator arithmetic below happily produces a nonsense pair from any
 * two ASCII letters, so callers need the null to mean "render no flag" rather
 * than painting a box.
 *
 * @example
 * getCountryFlagEmoji('IE') // '🇮🇪'
 * getCountryFlagEmoji('ZZ') // null
 */
export function getCountryFlagEmoji(
  countryCode: string | null | undefined
): string | null {
  const normalized = countryCode?.toUpperCase();
  if (!normalized || !SUPPORTED_COUNTRIES.has(normalized)) {
    return null;
  }

  // Regional indicator symbols live at U+1F1E6..U+1F1FF, i.e. 127397 above the
  // ASCII code point of the corresponding letter. A pair of them renders as the
  // country's flag.
  return String.fromCodePoint(
    ...normalized.split('').map(char => 127397 + char.charCodeAt(0))
  );
}

/**
 * True when a value is in the canonical CRM phone shape.
 *
 * Not every value that reaches a `phone_number` property has been normalized.
 * The CSV import review deliberately shows the raw cell from the user's file
 * ("+14155550102") beside the normalized CRM value, and a raw string has none of
 * the fields {@link formatPhoneNumber} reads — every one of its fallbacks ends at
 * `phone.national_number`, which is `undefined` on a string, so the value renders
 * as nothing at all.
 *
 * Callers use this to decide whether the phone renderer applies, instead of
 * casting an unknown value to `PhoneNumber` and hoping.
 */
export function isPhoneNumberValue(value: unknown): value is PhoneNumber {
  return (
    typeof value === 'object' &&
    value !== null &&
    'country_code' in value &&
    'national_number' in value
  );
}

/**
 * Format phone number for display using international format.
 * Returns "—" for null/undefined.
 *
 * @example
 * formatPhoneNumber({country_code: "US", national_number: "4155552671"})
 * // Returns: "+1 415 555 2671"
 */
export function formatPhoneNumber(
  phone: PhoneNumber | null | undefined
): string {
  if (!phone) {
    return '—';
  }

  try {
    const callingCode = getCountryCallingCode(
      phone.country_code as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string
      metadata as any // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string
    );
    const fullNumber = `+${callingCode}${phone.national_number}`;
    const parsed = parsePhoneNumber(fullNumber, metadata as any); // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string

    if (parsed && parsed.isValid()) {
      return parsed.formatInternational();
    }

    // Fallback: simple format with calling code
    return `+${callingCode} ${phone.national_number}`;
  } catch {
    // Fallback on any parsing error
    try {
      const callingCode = getCountryCallingCode(
        phone.country_code as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string
        metadata as any // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string
      );
      return `+${callingCode} ${phone.national_number}`;
    } catch {
      return phone.national_number;
    }
  }
}

/**
 * Validate phone number using libphonenumber-js.
 * Returns error message or null if valid.
 *
 * Backend validation requirements:
 * - country_code must be 2 letters
 * - national_number must be non-empty
 * - country_code must match national_number's detected region
 */
/**
 * Build a tel: URI for click-to-call functionality.
 * Returns tel:+{callingCode}{nationalNumber} (no spaces, E.164-ish format).
 *
 * @example
 * buildTelUri({country_code: "US", national_number: "4155552671"})
 * // Returns: "tel:+14155552671"
 */
export function buildTelUri(phone: PhoneNumber): string {
  try {
    const callingCode = getCountryCallingCode(
      phone.country_code as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string
      metadata as any // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string
    );
    return `tel:+${callingCode}${phone.national_number}`;
  } catch {
    // Fallback: just use national number if country code is invalid
    return `tel:${phone.national_number}`;
  }
}

export function validatePhone(phone: PhoneNumber): string | null {
  // Check required fields
  if (!phone.country_code || phone.country_code.length !== 2) {
    return 'Country code must be 2 letters';
  }

  if (!phone.national_number || phone.national_number.trim() === '') {
    return 'Phone number is required';
  }

  // Validate with libphonenumber-js
  try {
    const callingCode = getCountryCallingCode(
      phone.country_code as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string
      metadata as any // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string
    );
    const fullNumber = `+${callingCode}${phone.national_number}`;
    const parsed = parsePhoneNumber(fullNumber, metadata as any); // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js requires CountryCode type but our PhoneNumber model stores country_code as string

    if (!parsed) {
      return 'Invalid phone number format';
    }

    if (!parsed.isValid()) {
      return 'Invalid phone number for selected country';
    }

    // Check country code matches (backend validation)
    if (parsed.country && parsed.country !== phone.country_code) {
      return `Phone number does not match country ${phone.country_code}`;
    }

    return null; // Valid
  } catch {
    return 'Invalid phone number format';
  }
}
